import { SprintProductChapter, WorkDayProduct } from '@data';
import { getFullNameOrEmail, toFixedHours } from '@app/utils';

/**
 * The order and the emptiness rules the two views read by, in one place because both screens apply them
 * and the PRD asks the client and the manager to see the same sprint.
 *
 * Neither rule holds in the data as it arrives. `getBriefReports` builds roles in the order the reports
 * happen to arrive, and `getDaysArray` walks the sprint backwards and emits every calendar day whether
 * or not anyone reported on it. Sorting here rather than in those helpers keeps the existing callers of
 * both — the chapter tabs elsewhere — reading exactly as they do today.
 */

/** Roles that have time on them, most hours first. */
export const rolesByHours = (groups: SprintProductChapter[] | null): SprintProductChapter[] =>
  (groups ?? []).filter(group => group.totalMinutes > 0).sort((a, b) => b.totalMinutes - a.totalMinutes);

/**
 * Days that have time on them, earliest first. The keys are `yyyy-MM-dd`, so a lexical compare is a
 * chronological one.
 */
export const daysInSprintOrder = (days: WorkDayProduct[] | null): WorkDayProduct[] =>
  (days ?? []).filter(day => day.totalMinutes > 0).sort((a, b) => a.date.localeCompare(b.date));

/**
 * The sprint's total, summed over days rather than roles. Both views have to show the same figure; days
 * are the side that carries every report exactly once.
 */
export const totalMinutesOf = (days: WorkDayProduct[] | null): number =>
  (days ?? []).reduce((sum, day) => sum + day.totalMinutes, 0);

/**
 * The sprint's people as a block to paste into a spreadsheet: sprint number, name, hours, tab-separated,
 * most hours first.
 *
 * Flat, where the screen draws role cards. The sheet this feeds has a row per person and nowhere to put
 * a role subtotal, so someone who filed under two roles arrives as one row with the roles summed: two
 * rows carrying the same name would double that person in any formula over the column. Hours go out
 * bare, without the screen's `h`, because a spreadsheet reads a cell ending in a letter as text and
 * will not add it up.
 */
export const memberHoursTsv = (sprintNumber: number, groups: SprintProductChapter[] | null): string => {
  const byPerson = (groups ?? [])
    .flatMap(group => group.members)
    .reduce((people, { member, totalMinutes }) => {
      // Keyed on the person, not the membership: one person on two roles is two entries in `brief`.
      const key = member.user?.id ?? member.id;
      const seen = people.get(key);
      people.set(key, {
        name: seen?.name ?? getFullNameOrEmail(member.user),
        minutes: (seen?.minutes ?? 0) + totalMinutes,
      });
      return people;
    }, new Map<string, { name: string; minutes: number }>());

  return Array.from(byPerson.values())
    .filter(person => person.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes)
    .map(person => `${sprintNumber}\t${person.name}\t${toFixedHours(person.minutes / 60)}`)
    .join('\n');
};
